import { Alert, AlertIcon, Box, Grid, HStack, Text, VStack } from "@chakra-ui/react";
import PatientProfileEditModeButton from "../../../../entityProfileModal/components/EntityProfileEditModeButton";
import type { PatientProfileBridgeActions } from "../../../patientProfileModal.actions";
import type { PatientProfilePatient } from "../../../patientProfileModal.types";
import {
  AddEmergencyContactButton,
  EmergencyContactCard,
  EmergencyContactEditorCard,
  NoEmergencyContactsRow,
} from "./components/EmergencyContactCards";
import { EmergencyPreparednessBlock } from "./components/EmergencyPreparednessBlock";
import { EmergencyPreparednessFields } from "./components/EmergencyPreparednessFields";
import { usePatientProfileEmergencyPreparednessController } from "./PatientProfileEmergencyPreparednessSection.hooks";
import EntityModalButton from "../../../../entityProfileModal/components/EntityModalButton";

type Props = {
  actions: PatientProfileBridgeActions;
  patient: PatientProfilePatient;
};

export default function PatientProfileEmergencyPreparednessSection(props: Props) {
  const { patient } = props;
  const controller = usePatientProfileEmergencyPreparednessController(patient);
  const patientMainPhoneNumber =
    (patient as { mainPhoneNumber?: string | null }).mainPhoneNumber ?? null;

  return (
    <VStack alignItems="stretch" spacing={8}>
      {controller.canEdit ? (
        <HStack justifyContent="flex-end">
          {controller.isEditingContacts ? (
            <EntityModalButton
              isIconOnly
              aria-label="Save emergency contacts"
              iconClassName="fa fa-check"
              isLoading={controller.isSaving}
              kind="blue"
              size="sm"
              title="Save emergency contacts"
              onClick={controller.saveEmergencyContacts}
            />
          ) : null}
          <PatientProfileEditModeButton
            isDisabled={controller.isSaving}
            isEditing={controller.isEditingContacts}
            label="Edit"
            onClick={
              controller.isEditingContacts ? controller.stopEditing : controller.startEditing
            }
          />
        </HStack>
      ) : null}

      <EmergencyPreparednessBlock title="Emergency Information">
        {controller.isEditingContacts ? (
          <VStack alignItems="stretch" gridColumn="1 / -1" spacing={4}>
            {controller.contactsValidationMessage ? (
              <Alert status="error">
                <AlertIcon />
                <Text>{controller.contactsValidationMessage}</Text>
              </Alert>
            ) : null}
            {controller.draftEmergencyContacts.map((contact, index) => (
              <EmergencyContactEditorCard
                key={`emergency-contact-${index}`}
                contact={contact}
                index={index}
                isDisabled={controller.isSaving}
                isOnboardingAgency={controller.isOnboardingAgency}
                mainPhoneNumber={patientMainPhoneNumber}
                showErrors={controller.contactsValidationMessage !== null}
                onChange={controller.updateEmergencyContact}
                onRemove={controller.removeEmergencyContact}
                onUpdateMainPhoneNumber={controller.updateMainPhoneNumber}
              />
            ))}
            {controller.draftEmergencyContacts.length === 0 ? (
              <NoEmergencyContactsRow
                isDisabled={controller.isSaving}
                isEditing={true}
                onAdd={controller.addEmergencyContact}
              />
            ) : (
              <Box>
                <AddEmergencyContactButton
                  isDisabled={controller.isSaving}
                  onClick={controller.addEmergencyContact}
                />
              </Box>
            )}
          </VStack>
        ) : controller.emergencyContacts.length === 0 ? (
          <NoEmergencyContactsRow
            isDisabled={controller.isSaving}
            isEditing={false}
            onAdd={controller.addEmergencyContact}
          />
        ) : (
          <Grid
            gap={4}
            gridColumn="1 / -1"
            templateColumns={{ base: "1fr", xl: "repeat(2, minmax(0, 1fr))" }}
          >
            {controller.emergencyContacts.map((contact, index) => (
              <EmergencyContactCard
                key={`emergency-contact-${index}`}
                actions={props.actions}
                contact={contact}
                index={index}
                isOnboardingAgency={controller.isOnboardingAgency}
                mainPhoneNumber={patientMainPhoneNumber}
              />
            ))}
          </Grid>
        )}
      </EmergencyPreparednessBlock>

      <EmergencyPreparednessBlock
        actions={
          controller.canEdit ? (
            <PatientProfileEditModeButton
              isDisabled={controller.isSaving}
              isEditing={controller.isEditingPreparedness}
              label="Edit"
              onClick={
                controller.isEditingPreparedness
                  ? controller.stopEditingPreparedness
                  : controller.startEditingPreparedness
              }
            />
          ) : null
        }
        title="Emergency Preparedness"
      >
        <EmergencyPreparednessFields
          activeField={controller.activePreparednessField}
          draft={controller.preparednessDraft}
          isEditing={controller.isEditingPreparedness}
          patient={patient}
          onActivate={controller.setActivePreparednessField}
          onCancel={controller.cancelPreparednessField}
          onChangeDraft={controller.setPreparednessDraft}
          onCommit={controller.commitPreparednessField}
        />
      </EmergencyPreparednessBlock>
    </VStack>
  );
}
